import { proposalsService } from '@/services/proposals';
import { useQueries } from '@tanstack/react-query';
import {
  PROPOSAL_STATUS_GROUPS,
  groupStatusValue,
} from '../proposal-status-groups';

/**
 * Cada chave é o valor do filtro `status`: '' (total) ou a lista de códigos do
 * grupo (ex.: '9,18'). Assim a contagem de cada card respeita o agrupamento.
 */
const STATUS_KEYS: string[] = [
  '',
  ...PROPOSAL_STATUS_GROUPS.map(groupStatusValue),
];

type StatusCountEntry = {
  status: string;
  total: number;
};

interface UseProposalStatusCountsParams {
  partyCode?: string;
  productCode?: string;
  proposalDateFrom?: string;
  proposalDateTo?: string;
}

/**
 * Contagem de propostas por status (incluindo o total) respeitando os filtros
 * de corretor/produto/período. Espelha `useQuoteStatusCounts`: uma query por
 * status com `pageSize: 1`, lendo `meta.total`.
 */
export function useProposalStatusCounts({
  partyCode,
  productCode,
  proposalDateFrom,
  proposalDateTo,
}: UseProposalStatusCountsParams = {}) {
  const results = useQueries({
    queries: STATUS_KEYS.map(status => ({
      queryKey: [
        'proposals-status-count',
        status,
        partyCode,
        productCode,
        proposalDateFrom,
        proposalDateTo,
      ],
      queryFn: async () => {
        const response = await proposalsService.getProposals({
          pageSize: 1,
          pageNumber: 1,
          status,
          partyCode,
          productCode,
          proposalDateFrom,
          proposalDateTo,
        });
        if (!response.success || !response.data) return 0;
        return response.data.meta?.total ?? 0;
      },
      staleTime: 30_000,
    })),
  });

  const counts: StatusCountEntry[] = STATUS_KEYS.map((status, i) => ({
    status,
    total: results[i].data ?? 0,
  }));

  const isLoading = results.some(r => r.isLoading);

  return { counts, isLoading };
}
